import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, Loader2, Search, Plus, Edit, Eye, MapPin, TrendingUp,
  Trash2, EyeOff, Star, StarOff
} from 'lucide-react';

const AdminOpportunitiesPage = () => {
  const { formatCurrency } = useI18n();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: '' });

  useEffect(() => { fetchOpportunities(); }, [filters]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      const response = await api.get('/admin/opportunities', { params });
      let data = response.data.opportunities || [];
      if (filters.search) {
        const s = filters.search.toLowerCase();
        data = data.filter(o => o.title?.toLowerCase().includes(s) || o.location_city?.toLowerCase().includes(s));
      }
      setOpportunities(data);
      setTotal(response.data.total || data.length);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (opp) => {
    const newStatus = opp.status === 'published' ? 'draft' : 'published';
    const label = newStatus === 'published' ? 'publicada' : 'ocultada';
    try {
      await api.put(`/admin/opportunities/${opp.opportunity_id}`, {
        ...opp,
        status: newStatus,
        is_featured: newStatus === 'draft' ? false : opp.is_featured,
      });
      toast.success(`Oportunidad ${label}`);
      fetchOpportunities();
    } catch (err) {
      toast.error('Error al cambiar visibilidad');
    }
  };

  const toggleFeatured = async (opp) => {
    try {
      await api.put(`/admin/opportunities/${opp.opportunity_id}`, {
        ...opp,
        is_featured: !opp.is_featured,
      });
      toast.success(opp.is_featured ? 'Quitada de destacados' : 'Marcada como destacada');
      fetchOpportunities();
    } catch (err) {
      toast.error('Error al cambiar destacada');
    }
  };

  const deleteOpportunity = async (opp) => {
    const title = opp.title || 'esta oportunidad';
    if (!confirm(`¿Eliminar "${title}" permanentemente? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/opportunities/${opp.opportunity_id}`);
      toast.success('Oportunidad eliminada');
      fetchOpportunities();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al eliminar';
      toast.error(detail);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { className: 'bg-muted text-muted-foreground border-muted', label: 'Borrador' },
      published: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Publicada' },
      under_review: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'En Revisión' },
      open: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Abierta' },
      soft_circle: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Soft Circle' },
      fully_funded: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Fondeada' },
      closed: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cerrada' },
      archived: { className: 'bg-muted text-muted-foreground border-muted', label: 'Archivada' },
    };
    const style = styles[status] || styles.draft;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-opportunities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-light tracking-tight mb-1">Gestión de Oportunidades</h1>
          <p className="text-sm text-muted-foreground">{total} oportunidades en el sistema</p>
        </div>
        <Link to="/admin/opportunities/new">
          <Button className="gap-2" data-testid="new-opportunity-btn">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Nueva Oportunidad
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o ubicación..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="published">Publicada</SelectItem>
                <SelectItem value="under_review">En Revisión</SelectItem>
                <SelectItem value="open">Abierta</SelectItem>
                <SelectItem value="soft_circle">Soft Circle</SelectItem>
                <SelectItem value="fully_funded">Fondeada</SelectItem>
                <SelectItem value="closed">Cerrada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : opportunities.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground mb-4">No hay oportunidades</p>
            <Link to="/admin/opportunities/new">
              <Button>Crear Primera Oportunidad</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {opportunities.map((opp) => (
            <Card key={opp.opportunity_id} className="card-dashboard hover:border-primary/30 transition-colors" data-testid={`opp-card-${opp.opportunity_id}`}>
              <CardContent className="p-4 lg:p-6">
                <div className="flex flex-col gap-4">
                  {/* Top row: status + title */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getStatusBadge(opp.status)}
                      {opp.project_code && <span className="text-xs text-muted-foreground">{opp.project_code}</span>}
                      {opp.is_featured && (
                        <Badge variant="outline" className="border-primary/30 text-primary text-xs">Destacada</Badge>
                      )}
                    </div>
                    <h3 className="font-medium mb-1">{opp.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      {opp.location_city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                          {opp.location_city}{opp.location_state ? `, ${opp.location_state}` : ''}
                        </span>
                      )}
                      {opp.projected_irr_base && (
                        <span className="flex items-center gap-1">
                          <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                          TIR {opp.projected_irr_base}%{opp.projected_irr_upside ? `-${opp.projected_irr_upside}%` : ''}
                        </span>
                      )}
                      {opp.capital_target > 0 && (
                        <span>Capital: {formatCurrency(opp.capital_target)}</span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                    {/* Visibility toggle */}
                    <Button
                      variant={opp.status === 'published' ? 'outline' : 'default'}
                      size="sm"
                      className="gap-1.5 text-xs"
                      onClick={() => toggleVisibility(opp)}
                      data-testid={`toggle-visibility-${opp.opportunity_id}`}
                    >
                      {opp.status === 'published' ? (
                        <><EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />Ocultar</>
                      ) : (
                        <><Eye className="h-3.5 w-3.5" strokeWidth={1.5} />Publicar</>
                      )}
                    </Button>

                    {/* Featured toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      className={`gap-1.5 text-xs ${opp.is_featured ? 'border-primary/40 text-primary' : ''}`}
                      onClick={() => toggleFeatured(opp)}
                      data-testid={`toggle-featured-${opp.opportunity_id}`}
                    >
                      {opp.is_featured ? (
                        <><StarOff className="h-3.5 w-3.5" strokeWidth={1.5} />Quitar Destacada</>
                      ) : (
                        <><Star className="h-3.5 w-3.5" strokeWidth={1.5} />Destacar</>
                      )}
                    </Button>

                    {/* View */}
                    <Link to={`/oportunidades/${opp.slug}`} target="_blank">
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                        <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />Ver
                      </Button>
                    </Link>

                    {/* Edit */}
                    <Link to={`/admin/opportunities/${opp.opportunity_id}/edit`}>
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                        <Edit className="h-3.5 w-3.5" strokeWidth={1.5} />Editar
                      </Button>
                    </Link>

                    {/* Delete */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 ml-auto"
                      onClick={() => deleteOpportunity(opp)}
                      data-testid={`delete-${opp.opportunity_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />Eliminar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOpportunitiesPage;
